import { defineStore } from 'pinia'
import { reactive } from 'vue'

interface ValidationQueueItem {
  /**
   * validation の管轄区分の識別子
   * @description ValidationProvider コンポーネントに渡す識別子。ValidationProvider コンポーネント側は、この値を見て自身に発火したバリデーションかどうかを判断する。
   * @description form submit 時のバリデーションの場合は form 要素の name 属性値や、form submit 以外の場合は任意の識別子（トリガーされた際にどこの要素が一度にバリデーション判定されるかの区分を示す）等の値が入る。
   */
  namespace: string,

  fields: string[],

  /**
   * バリデーション対象の各入力要素のフィールド名
   * @description バリデーション発火のタイミングでバリデーションを行う対象すべての識別子をここに登録し、バリデーション通過したものから削除されていく。
   * @description 対象の各 input の name プロパティをつなげた配列等。
   */
  invalidFields: string[],

  /**
   * バリデーショントリガーが発火してからバリデーションチェックが完了した要素のフィールド名
   * @description バリデーションチェックした要素が登録されるので、バリデーション通過（異常なし）かどうかは問われない。
   */
  checkedFields: string[],

  /**
   * バリデーションの発火フラグ
   */
  run: boolean,
}

interface ValidationContext {
  queue: ValidationQueueItem[],
  observe: boolean,
  progressNamespace: string,
}

export const useValidationStore = defineStore('validation', () => {
  const context: ValidationContext = reactive({
    queue: [],
    observe: false,
    progressNamespace: '',
  })

  const init = (namespace: string, fields: string[]) => {
    const queue = getQueue(namespace)

    if (queue) {
      console.warn('validation queue already exists.')
      return
    }

    if (!!fields && fields.length) {
      context.queue.push({
        namespace: namespace,
        fields: fields,
        invalidFields: [],
        checkedFields: [],
        run: false,
      })
    }
  }

  const stopValidation = (namespace: string) => {
    if (getQueue(namespace)) {
      context.observe = false
      getQueue(namespace)!.run = false
    }
  }

  /**
   * バリデーションキューの登録
   * @param namespace 対象の validation の管轄区分の識別子
   * @param invalidFields 対象の識別子の配列（field 名称）
   */
  const setQueue = (namespace: string, invalidFields: string[]) => {
    const queue = getQueue(namespace)
    // すでに指定の namespace がキューに存在している場合は現存キューに結合
    if (queue) {
      const array = new Set(queue.invalidFields.concat(invalidFields))
      queue.invalidFields = [ ...array ]
      queue.run = true
    } else {
      context.queue.push({
        namespace: namespace,
        fields: invalidFields,
        invalidFields: invalidFields,
        checkedFields: [],
        run: true,
      })
    }

    context.observe = true
    context.progressNamespace = namespace
  }

  /**
   * 指定の validation 管理区分のバリデーションキュー情報を取得する
   * @param namespace 対象の validation の管轄区分の識別子
   */
  const getQueue = (namespace: string): ValidationQueueItem | undefined => {
    if (!namespace) return undefined
    return context.queue.find(v => v.namespace === namespace)
  }

  const clearQueue = (namespace: string) => {
    if (!namespace) return
    const queue = getQueue(namespace)
    if (queue) {
      queue.checkedFields = []
      queue.invalidFields = []
      queue.run = false
    }
  }

  /**
   * 指定の管理区分のバリデーションキューが登録されているかどうか
   * @param namespace 対象の validation の管轄区分の識別子
   * @returns true 指定対象のキューが登録されている / false 登録されていない
   */
  const checkQueueStacking = (namespace: string): boolean => {
    // namespace の指定がない場合は false
    if (!namespace) return false

    return !!(getQueue(namespace) && getQueue(namespace)?.invalidFields.length)
  }

  const completed = (namespace: string, fieldName: string) => {
    if (namespace && fieldName) {
      const queue = getQueue(namespace)

      if (queue) {
        const array = new Set(queue.checkedFields.concat(fieldName))
        queue.checkedFields = [ ...array ]
      }
    }
  }

  /**
   * 指定の項目がバリデーション通過した際の処理
   * @description バリデーションキューに登録された field 名称が取り下げられる。
   *
   * @param namespace 対象の validation の管轄区分の識別子
   * @param fieldName 対象の識別子（field 名称）
   */
  const valid = (namespace: string, fieldName: string) => {
    if (namespace && fieldName) {
      const queue = getQueue(namespace)

      if (queue) {
        queue.invalidFields = queue.invalidFields.filter(name => name !== fieldName)
      }
    }
  }

  return {
    // data
    context,

    // methods
    init,
    stopValidation,
    setQueue,
    clearQueue,
    getQueue,
    checkQueueStacking,
    completed,
    valid,
  }
})
